import React, { useEffect, useRef } from "react";
import { HowWeWorkStyled } from "./HowWeWorkStyled";
import HowWeWorkImg from "../../../../assets/images/HowWeWork.png";
import { gsap } from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";

gsap.registerPlugin(ScrollTrigger);

const HowWeWork = () => {
  const sectionRef = useRef<HTMLElement>(null);
  const contentRef = useRef<HTMLDivElement>(null);
  const imageSideRef = useRef<HTMLDivElement>(null);
  const statsRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const section = sectionRef.current;
    const content = contentRef.current;
    const imageSide = imageSideRef.current;
    const stats = statsRef.current;

    if (!section || !content || !imageSide) return;

    const ctx = gsap.context(() => {
      // Content side animation
      gsap.fromTo(
        content.querySelectorAll(".sectionLabel, h1, p"),
        { opacity: 0, y: 40 },
        {
          opacity: 1,
          y: 0,
          duration: 0.8,
          stagger: 0.15,
          ease: "power3.out",
          scrollTrigger: {
            trigger: section,
            start: "top 75%",
            toggleActions: "play none none reverse",
          },
        },
      );

      // CTA buttons animation
      gsap.fromTo(
        content.querySelectorAll(".ctaWrapper a"),
        { opacity: 0, y: 20 },
        {
          opacity: 1,
          y: 0,
          duration: 0.6,
          stagger: 0.1,
          delay: 0.4,
          ease: "power3.out",
          scrollTrigger: {
            trigger: section,
            start: "top 75%",
            toggleActions: "play none none reverse",
          },
        },
      );

      // Stats animation
      if (stats) {
        gsap.fromTo(
          stats.querySelectorAll(".statItem"),
          { opacity: 0, y: 30 },
          {
            opacity: 1,
            y: 0,
            duration: 0.6,
            stagger: 0.1,
            ease: "power3.out",
            scrollTrigger: {
              trigger: stats,
              start: "top 85%",
              toggleActions: "play none none reverse",
            },
          },
        );
      }

      // Image side animation
      gsap.fromTo(
        imageSide.querySelector(".imageWrapper"),
        { opacity: 0, scale: 0.9 },
        {
          opacity: 1,
          scale: 1,
          duration: 1,
          ease: "power3.out",
          scrollTrigger: {
            trigger: section,
            start: "top 70%",
            toggleActions: "play none none reverse",
          },
        },
      );

      // Floating cards animation
      gsap.fromTo(
        imageSide.querySelectorAll(".floatingCard"),
        { opacity: 0, y: 30, scale: 0.9 },
        {
          opacity: 1,
          y: 0,
          scale: 1,
          duration: 0.6,
          stagger: 0.2,
          delay: 0.5,
          ease: "back.out(1.7)",
          scrollTrigger: {
            trigger: section,
            start: "top 70%",
            toggleActions: "play none none reverse",
          },
        },
      );
    }, section);

    return () => ctx.revert();
  }, []);

  return (
    <HowWeWorkStyled id="work" ref={sectionRef}>
      <div className="howWeWorkContainer">
        <div className="contentSide" ref={contentRef}>
          <div className="sectionLabel">
            <span className="line" />
            <span>Our Process</span>
          </div>

          <h1>
            How <span className="outline">We</span> Work
          </h1>

          <p>
            We believe in building lasting partnerships through transparent
            communication and exceptional service delivery. Let's discuss your
            technology needs.
          </p>

          <div className="ctaWrapper">
            <a href="#contactUs" className="primaryBtn">
              Get in Touch
              <svg
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
              >
                <path d="M5 12h14M12 5l7 7-7 7" />
              </svg>
            </a>
            <a href="#services" className="secondaryBtn">
              Learn More
            </a>
          </div>

          <div className="statsRow" ref={statsRef}>
            <div className="statItem">
              <div className="number">
                500<span>+</span>
              </div>
              <div className="label">Happy Clients</div>
            </div>
            <div className="statItem">
              <div className="number">
                20<span>+</span>
              </div>
              <div className="label">Years Experience</div>
            </div>
            <div className="statItem">
              <div className="number">
                99<span>%</span>
              </div>
              <div className="label">Satisfaction</div>
            </div>
          </div>
        </div>

        <div className="imageSide" ref={imageSideRef}>
          <div className="imageWrapper">
            <img src={HowWeWorkImg} alt="How we work" />
          </div>

          <div className="floatingCard card1">
            <div className="cardIcon">
              <svg
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
              >
                <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
              </svg>
            </div>
            <div className="cardText">
              <div className="value">Trusted</div>
              <div className="label">HP Authorized Partner</div>
            </div>
          </div>

          <div className="floatingCard card2">
            <div className="cardIcon">
              <svg
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
              >
                <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" />
                <polyline points="22 4 12 14.01 9 11.01" />
              </svg>
            </div>
            <div className="cardText">
              <div className="value">Quality</div>
              <div className="label">Certified Service</div>
            </div>
          </div>
        </div>
      </div>
    </HowWeWorkStyled>
  );
};

export default HowWeWork;
